import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import qs from 'qs';
import {
  Button,
  Checkbox,
  Dropdown,
  Grid,
  Header,
  Icon,
  Modal,
  Segment,
} from 'semantic-ui-react';
import { flashErrorMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';

export default class ImportFromProjectModal extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    trigger: PropTypes.node,
    onConfirm: PropTypes.func,
    projectID: PropTypes.string,
  }
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      onClose: false,
      loading: false,
      disableTemplate: true,
      projectOpts: [],
      selectedProjectID: null,
      selectedTemplateIDs: [],
      templates: [],
      errMessages: [],
    };
  }
  componentDidMount() {
    this.fetchProjects();
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  setComponentState = (state = {}) => (new Promise((res) => {
    this.setState(state, () => {
      res(state);
    });
  }))
  async fetchProjects() {
    const { store } = this.context;
    const { projectID } = this.props;
    await this.setLoading(true);
    const query = {
      limit: 0,
      showAsOptions: true,
    };
    let res;
    try {
      res = await store.dispatch(fetchM(`/docxtemplates/projects/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (err) {
      if (err.res && err.res.status >= 399 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
      }
      await this.setLoading(false);
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: i18next.t('There was an error with your request. Please try again.', { ns: 'translation' }) }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const data = await res.json();
      let { projectOpts } = this.state;
      if (data && data.length > 0) {
        projectOpts = data
          .filter(d => d.id !== projectID && d.statusID !== 'deleted')
          .map(d => ({ value: d.id, text: d.name }));
      }
      await this.setComponentState({ projectOpts });
    }
    await this.setLoading(false);
  }
  async fetchProjectTemplates() {
    await this.setLoading(true);
    const { selectedProjectID } = this.state;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/docxtemplates/projects/${selectedProjectID}/templates/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: i18next.t('An error occurred, please refresh page', { ns: 'translation' }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const data = await res.json();
      // select all by default
      const templates = (data || [])
      .filter(template =>
        template.docxFileKey &&
        template.docxFileKey.indexOf('templates/default_templates/') !== 0
      )
      .map(template => ({
        ...template,
        selected: true,
      }));
      this.setState({
        templates,
        selectedTemplateIDs: templates.map(t => t.id),
      });
    }
    await this.setLoading(false);
  }
  handleOnOpen = () => {
    this.setState({ open: true });
  }
  handleOnClose = () => {
    this.setState({
      open: false,
      templates: [],
      selectedProjectID: null,
      selectedTemplateIDs: [],
    });
  }
  handleOnDropdownChange = (e, { value }) => {
    this.setState({
      selectedProjectID: value,
      templates: [],
      selectedTemplateIDs: [],
    }, () => {
      this.fetchProjectTemplates();
    });
  };
  handleOnCheckboxChange = (e, { value }) => {
    const { templates, selectedTemplateIDs } = this.state;
    const updatedTemplates = templates.map((template) => {
      if (template.id === value) {
        return { ...template, selected: !template.selected };
      }
      return template;
    });

    const isSelected = selectedTemplateIDs.includes(value);
    const updatedSelectedValues = isSelected
      ? selectedTemplateIDs.filter(v => v !== value)
      : [...selectedTemplateIDs, value];

    this.setState({
      templates: updatedTemplates,
      selectedTemplateIDs: updatedSelectedValues,
    });
  };
  handleOnConfirm = (e) => {
    e.preventDefault();
    const { selectedProjectID, selectedTemplateIDs } = this.state;
    const { onConfirm } = this.props;
    const body = {
      project_id: selectedProjectID,
      template_ids: selectedTemplateIDs,
    };
    if (onConfirm) {
      onConfirm(body);
    }
    this.handleOnClose();
  }
  renderContent = () => {
    const { templates, selectedProjectID } = this.state;
    if (templates.length > 0) {
      return (
        <Segment style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {this.renderTemplateCheckboxes()}
        </Segment>
      );
    }
    if (selectedProjectID) {
      return <i>{i18next.t('No templates found', { ns: 'translation' })}</i>;
    }
    return <i>{i18next.t('Please select a project first', { ns: 'translation' })}</i>;
  }
  renderTemplateCheckboxes = () => {
    const { templates } = this.state;
    return templates.map(template => (
      <Grid key={template.id}>
        <Grid.Row>
          <Grid.Column floated="left" width={10}>
            <span>{template.name}</span>
          </Grid.Column>
          <Grid.Column floated="right" width={2}>
            <Checkbox
              value={template.id}
              checked={template.selected}
              onChange={this.handleOnCheckboxChange}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    ));
  }

  render() {
    const { loading, open, projectOpts, selectedProjectID, selectedTemplateIDs } = this.state;
    const { trigger } = this.props;
    return (
      <Modal
        size="tiny"
        onOpen={this.handleOnOpen}
        onClose={() => this.handleOnClose()}
        open={open}
        trigger={trigger}
      >
        <Header as="h3" icon="file" align="content-center" content={i18next.t('Import Template from other Project', { ns: 'translation' })} />
        <Modal.Content>
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <Header as="h5" content={i18next.t('Import from', { ns: 'translation' })} />
                <Dropdown
                  name="selectedProjectID"
                  placeholder={i18next.t('Select Project', { ns: 'screens' })}
                  fluid
                  search
                  selection
                  options={projectOpts}
                  onChange={this.handleOnDropdownChange}
                  value={selectedProjectID || ''}
                  noResultsMessage={i18next.t('No results found', { ns: 'translation' })}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column className={!selectedProjectID ? 'disabled' : ''}>
                <Header as="h5" content={i18next.t('Templates', { ns: 'translation' })} />
                {this.renderContent()}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button
            compact
            color="red"
            onClick={this.handleOnClose}
          >
            <Icon name="close" />
            {i18next.t('Cancel', { ns: 'translation' })}
          </Button>
          <Button
            compact
            color="green"
            onClick={this.handleOnConfirm}
            disabled={loading || selectedTemplateIDs.length < 1}
          >
            <Icon name="check" />
            {i18next.t('Confirm', { ns: 'translation' })}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ImportFromProjectModal.jsx